import { Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui";
import type { ProductRoleAssignment } from "~/features/admin/users/types";

interface ProductWithRoles {
  id: string;
  name: string;
  productRoles: ReadonlyArray<{ role: { id: string; name: string } }>;
}

interface ProductRoleAssignmentsFieldProps {
  products: ReadonlyArray<ProductWithRoles>;
  selectedRoles: ReadonlyMap<string, ProductRoleAssignment>;
  onChange: (productId: string, roleId: string | null) => void;
}

type Pending = { productId: string | null } | null;

export function ProductRoleAssignmentsField({ products, selectedRoles, onChange }: ProductRoleAssignmentsFieldProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending>(null);

  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const committedRows = useMemo(
    () =>
      Array.from(selectedRoles.values()).flatMap((assignment) => {
        const product = productById.get(assignment.productId);
        return product ? [{ product, roleId: assignment.roleId }] : [];
      }),
    [selectedRoles, productById],
  );

  const unassignedProducts = useMemo(() => products.filter((p) => !selectedRoles.has(p.id)), [products, selectedRoles]);

  const pendingProduct = pending?.productId ? (productById.get(pending.productId) ?? null) : null;

  useEffect(() => {
    if (pending?.productId && selectedRoles.has(pending.productId)) {
      setPending(null);
    }
  }, [pending, selectedRoles]);

  const commitPending = (productId: string, roleId: string) => {
    onChange(productId, roleId);
    setPending(null);
  };

  return (
    <div className="space-y-2">
      {committedRows.map(({ product, roleId }) => (
        <div key={product.id} className="flex items-center gap-2" data-testid={`product-role-row-${product.id}`}>
          <div className="w-40 shrink-0 text-14-regular text-fg-1">{product.name}</div>
          <Select value={roleId} onValueChange={(value) => onChange(product.id, value || null)}>
            <SelectTrigger className="h-9 flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {product.productRoles.map(({ role }) => (
                <SelectItem key={role.id} value={role.id}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("feature.users.removeProductRole")}
            onClick={() => onChange(product.id, null)}
          >
            <X />
          </Button>
        </div>
      ))}

      {pending && (
        <div className="flex items-center gap-2" data-testid="product-role-pending-row">
          <Select value={pending.productId ?? ""} onValueChange={(value) => setPending({ productId: value || null })}>
            <SelectTrigger className="h-9 flex-1">
              <SelectValue placeholder={t("feature.users.selectProduct")} />
            </SelectTrigger>
            <SelectContent>
              {unassignedProducts.map((product) => (
                <SelectItem key={product.id} value={product.id}>
                  {product.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value=""
            disabled={!pendingProduct}
            onValueChange={(value) => {
              if (pendingProduct && value) commitPending(pendingProduct.id, value);
            }}
          >
            <SelectTrigger className="h-9 flex-1">
              <SelectValue placeholder={t("feature.users.selectRole")} />
            </SelectTrigger>
            <SelectContent>
              {pendingProduct?.productRoles.map(({ role }) => (
                <SelectItem key={role.id} value={role.id}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("feature.users.cancelAddProductRole")}
            onClick={() => setPending(null)}
          >
            <X />
          </Button>
        </div>
      )}

      {!pending && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={unassignedProducts.length === 0}
          onClick={() => setPending({ productId: null })}
        >
          <Plus />
          <span>{t("feature.users.addProductRole")}</span>
        </Button>
      )}
    </div>
  );
}
